import jsQR from 'jsqr';

export const MAX_IMAGE_SIZE = 1600;

/**
 * Decode a QR code from raw RGBA pixels. Pure; safe in tests.
 * @param {{ data: Uint8ClampedArray, width: number, height: number }} imageData
 * @returns {string|null} decoded text or null
 */
export function decodeQrFromImageData(imageData) {
    if (!imageData || !imageData.data || !imageData.width || !imageData.height) {
        return null;
    }
    const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'attemptBoth' });
    const text = code && typeof code.data === 'string' ? code.data.trim() : '';
    return text ? text : null;
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Could not load image'));
        };
        img.src = url;
    });
}

/**
 * Decode a QR code from an image File chosen by the member. The image never leaves
 * the browser: it is drawn on an off-screen canvas, decoded, and discarded.
 * @param {File} file
 * @param {{ maxSize?: number }} opts  Longest side after downscaling (default MAX_IMAGE_SIZE).
 * @returns {Promise<string|null>}
 */
export async function decodeQrFromImageFile(file, { maxSize = MAX_IMAGE_SIZE } = {}) {
    if (!file) {
        return null;
    }
    const img = await loadImage(file);
    const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }
    ctx.drawImage(img, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    return decodeQrFromImageData(imageData);
}
